import React, { FC, useState, useEffect, useId, useCallback } from 'react';
import { fetchAll } from 'config/fetchAll';
import { useTranslation } from 'react-i18next';
import { useModalDialog } from 'reusable/useModalDialog';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';

export interface Task {
  id: number;
  name: string;
  icon: string;
  isDisabled: boolean;
}

interface TaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tasks?: Task[];
  /**
   * 選んだ作業（PR #258 の Codex の P1、FR-AC8）。ID ではなく、このダイアログが読んで見せた作業そのものを返す。
   * 以前は ID だけを返し、親が別に持つ一覧から引き直していたので、親の一覧に無い作業は黙って捨てられた。
   */
  onAddTasks: (selectedTasks: Task[]) => void;
}

const AddTaskDialog: FC<TaskDialogProps> = ({
  isOpen,
  onClose,
  tasks = [],
  onAddTasks,
}) => {
  const { t } = useTranslation('client');
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>(tasks);

  // EV-322（ADM-15）。候補を読めなかったこと（空の候補と区別する）と、失敗の後に読み終えるまで
  // 追加を止める印。最後の要求の応答だけを反映する（閉じた後・条件が変わった後の応答は捨てる）。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [failedBefore, setFailedBefore] = useState(false);
  const beginRequest = useLatestRequest();

  const fetchTasksData = useCallback(async () => {
    const isCurrent = beginRequest();
    setFetchError(null);
    try {
      // BE-21。以前は15件で切れていた。全件をページ順に読む。
      const list = await fetchAll<Task>('/v1/tasks/');
      if (!isCurrent()) return;
      setAllTasks(list);
      setFailedBefore(false);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching tasks data:', error);
      setAllTasks([]);
      setFetchError(error);
      setFailedBefore(true);
    }
  }, [beginRequest]);

  useEffect(() => {
    if (!isOpen) {
      // EV-333（AT-AC1）。閉じたら、読み込みの途中の応答を捨て、失敗と失敗の印を消す（編集の画面と同じ）。
      // 開き直したときは初めて開いたときと同じ扱いにする（前回の失敗の「読み込み中」・追加を止めるを持ち越さない）。
      beginRequest();
      setFetchError(null);
      setFailedBefore(false);
      return;
    }
    fetchTasksData();
    setSelectedTasks(tasks);
  }, [isOpen, tasks, fetchTasksData, beginRequest]);

  const handleCheckboxChange = (task: Task, isChecked: boolean) => {
    setSelectedTasks((prevSelectedTasks) => {
      if (isChecked) {
        return [...prevSelectedTasks, task];
      } else {
        return prevSelectedTasks.filter(
          (selectedTask) => selectedTask.id !== task.id
        );
      }
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // EV-322。候補を読めないまま（失敗の後、再読み込みの間も）追加しない。
    if (failedBefore) return;
    onAddTasks(selectedTasks);
    onClose();
  };

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[600px]"
          role="dialog"
          ref={dialogRef}
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('action.add_tasks')}
                  </h3>
                  <button type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form>
                  {fetchError ? (
                    <FetchFailed error={fetchError} onRetry={fetchTasksData} />
                  ) : failedBefore ? (
                    <p role="status" className="py-8 text-center text-gray-500">
                      {t('ui.loading', { ns: 'common' })}
                    </p>
                  ) : null}
                  <div className="grid grid-cols-3 gap-2 p-6">
                    {allTasks.map((task) => (
                      <TaskItem
                        key={task.id}
                        id={task.id}
                        name={task.name}
                        icon={task.icon}
                        isDisabled={
                          selectedTasks.find(
                            (selectedTask) => selectedTask.id === task.id
                          )?.isDisabled || false
                        }
                        isChecked={selectedTasks.some(
                          (selectedTask) => selectedTask.id === task.id
                        )}
                        onCheckboxChange={(isChecked) =>
                          handleCheckboxChange(task, isChecked)
                        }
                      />
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="button"
                      disabled={failedBefore}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                      onClick={handleSubmit}
                    >
                      {t('action.add_tasks')} ({selectedTasks.length})
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TaskItemProps {
  id: number;
  name: string;
  icon: React.ReactNode;
  isDisabled: boolean;
  onCheckboxChange: (isChecked: boolean) => void;
  isChecked: boolean;
}

const TaskItem: FC<TaskItemProps> = ({
  id,
  name,
  icon,
  isDisabled,
  onCheckboxChange,
  isChecked,
}) => {
  const [checked, setChecked] = useState(isChecked);
  const [iconSrc, setIconSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const importedIcon = await import(
          `../../../assets/icons/tasks/${icon}.svg`
        );
        setIconSrc(importedIcon.default);
      } catch (err) {
        console.error(`Icon not found: ${icon}`, err);
        setIconSrc(null);
      }
    };

    loadIcon();
  }, [icon]);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleCheckboxChange = () => {
    if (!isDisabled) {
      const newChecked = !checked;
      setChecked(newChecked);
      onCheckboxChange(newChecked);
    }
  };

  return (
    <label
      className={`flex flex-col items-start p-2 border rounded-md shadow-sm w-[175px] ${
        checked ? 'bg-secondary/100 border-neutral/75' : 'bg-white neutral/50'
      } ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''} `}
    >
      <span className="text-gray-900 font-medium text-body-2">{name}</span>
      <div className="flex items-end justify-between w-full">
        <input
          className="peer relative appearance-none shrink-0 w-5 h-5 border-2 border-neutral/50 rounded-sm mt-1 bg-white checked:bg-success checked:border-0 disabled:border-steel-400 disabled:bg-steel-400"
          type="checkbox"
          checked={checked}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
        />
        <svg
          className="absolute w-5 h-5 pointer-events-none hidden peer-checked:block stroke-white mt-1 outline-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="8 13 11 16 17 8"></polyline>
        </svg>
        {iconSrc && (
          <img src={iconSrc} alt={`${name} icon`} className="w-8 h-8" />
        )}
      </div>
    </label>
  );
};

export default AddTaskDialog;
